/* eslint-disable import/order */
import useMainMenuTranslationsFromInitialState from './hooks/useMainMenuTranslationsFromInitialState';
import { MainMenuWrapperProps } from './types';
import { getSourceEventParams } from '@crehana/analytics';
import {
  NOTIFICATIONS_FETCH,
  useGetNotification,
} from '@crehana/compass.notification';
import { useTranslation } from '@crehana/i18n';
import { useDisclosure } from '@crehana/react-hooks';
import { TopBanner } from '@crehana/web';
import { MAIN_MENU_TRANSLATION_KEY } from '@crehana/web/dist/MainMenu/constants';
import { MainMenuProvider } from '@crehana/web/dist/MainMenu/context';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';
import { useAuthDialog } from 'Jsx/ContextApis/AuthDialog';
import { useUITheme } from 'Jsx/ContextApis/UITheme';
import CookiesBanner from 'Jsx/CrehanaWeb/CookiesBanner';
import DialogGetPhone from 'Jsx/Shared/DialogGetPhone';
import useFeatureFlagQuery from 'Jsx/Shared/Hooks/useFeatureFlagQuery/useFeatureFlagQuery';
import { ContactSourceEnum } from 'Jsx/Types/Graphql/globals';
import React, { FC, useState } from 'react';

const MainMenuProviderWrapper: FC<MainMenuWrapperProps> = ({
  children,
  countryPrefix: countryPrefixProp,
  customLocales: customLocalesProp,
  isAnonymous,
  disableAuthDialog,
  topBannerProps,
  ...mainMenuProps
}) => {
  const shoppingCartData = useProductsContainer();
  const { theme, toggleTheme } = useUITheme();
  const { showLoginModal, showRegisterModal, preloadAuthDialog } =
    useAuthDialog();

  const { isActive: showNotification } = useFeatureFlagQuery({
    flagKey: 'user_notification_web',
  });

  const {
    notificationData,
    error,
    hasMore,
    loading: loadingNotification,
    onFetchMore,
    totalCount,
  } = useGetNotification({
    first: NOTIFICATIONS_FETCH,
    isSkip: !showNotification,
  });

  const { languageCode } = useTranslation();
  const customLocales = useMainMenuTranslationsFromInitialState();

  const onLogin = () => {
    showLoginModal(window.location.pathname + window.location.search);
  };

  const onRegister = () => {
    showRegisterModal(
      window.location.pathname + window.location.search,
      undefined,
      {
        isRenderedIn: mainMenuProps.amplitudePageName,
        ...getSourceEventParams(),
      },
      'Main Menu',
    );
  };

  const phoneLeadDialogState = useDisclosure(false);
  const [topBannerIsOpen, setTopBannerIsOpen] = useState(false);

  const onToggleTopBanner = (isOpen: boolean) => {
    setTopBannerIsOpen(isOpen);
  };

  const authProps = !disableAuthDialog
    ? { onLogin, onRegister, onAuthButtonHover: preloadAuthDialog }
    : {};

  const canRenderTopBanner = !mainMenuProps.layoutConfig?.hideTopBanner;
  const canRenderCookiesBanner = !mainMenuProps.layoutConfig?.hideCookiesBanner;

  return (
    <MainMenuProvider
      {...mainMenuProps}
      userTheme={theme}
      notification={{
        notificationData,
        error,
        hasMore,
        loading: loadingNotification,
        onFetchMore,
        totalCount,
        totalUnRead: notificationData.totalUnRead,
      }}
      onToggleUserTheme={toggleTheme}
      countryPrefix={countryPrefixProp || languageCode || ''}
      customLocales={{
        es: {
          [MAIN_MENU_TRANSLATION_KEY]: {
            ...customLocales.es?.[MAIN_MENU_TRANSLATION_KEY],
            ...customLocalesProp?.es?.[MAIN_MENU_TRANSLATION_KEY],
          },
        },
        pt: {
          [MAIN_MENU_TRANSLATION_KEY]: {
            ...customLocales.pt?.[MAIN_MENU_TRANSLATION_KEY],
            ...customLocalesProp?.pt?.[MAIN_MENU_TRANSLATION_KEY],
          },
        },
      }}
      topBannerIsOpen={topBannerIsOpen}
      shoppingCartData={shoppingCartData}
      onOpenPhoneDialog={phoneLeadDialogState.onOpen}
      layoutConfig={{
        ...mainMenuProps.layoutConfig,
        hideNotifications: !showNotification,
      }}
      {...authProps}
    >
      {canRenderTopBanner && (
        <TopBanner
          {...topBannerProps}
          amplitudePageName={mainMenuProps.amplitudePageName}
          onToggleTopBanner={onToggleTopBanner}
        />
      )}
      {children}
      {canRenderCookiesBanner && <CookiesBanner />}
      {phoneLeadDialogState.isOpen && (
        <DialogGetPhone
          hidenDialog={phoneLeadDialogState.onClose}
          sourceRender={ContactSourceEnum.SOURCE_SEARCH_POP_UP}
        />
      )}
    </MainMenuProvider>
  );
};

export default MainMenuProviderWrapper;
